import React, { useState } from "react";
import {
  useDeviceAlarms,
  useUpdateNotificationRead,
} from "hooks/useDevices";
import { useQueryClient } from "@tanstack/react-query";
import { AlarmsContainer } from "styles/components/deviceAlarms.styles";
import { DeviceAlarmsProps } from "types/deviceAlarms.types";
import AlarmModal from "./alarms/AlarmModal";
import AlarmTable from "./alarms/AlarmTable";
import { LoadingScreen } from "./shared/LoadingScreen";
import { PageError } from "./shared/PageError";

const DeviceAlarms: React.FC<DeviceAlarmsProps> = ({ serialNumber }) => {
  const { data, isLoading, error, refetch } = useDeviceAlarms(serialNumber);
  const updateNotificationRead = useUpdateNotificationRead();
  const queryClient = useQueryClient();
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedAlarm, setSelectedAlarm] = useState<any>(null);

  if (isLoading) {
    return <LoadingScreen message="Loading device alarms..." />;
  }

  if (error || !data || !data.success) {
    return (
      <PageError
        title="Error loading device alarms"
        message={
          error instanceof Error ? error.message : "Please try again later."
        }
        onRetry={() => refetch()}
      />
    );
  }

  const handleFixClick = (alarm: any) => {
    setSelectedAlarm(alarm);
    setModalVisible(true);
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await updateNotificationRead.mutateAsync(notificationId);
      queryClient.invalidateQueries({
        queryKey: ["deviceAlarms", serialNumber],
      });
      setModalVisible(false);
      setSelectedAlarm(null);
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  const handleModalHide = () => {
    setModalVisible(false);
    setSelectedAlarm(null);
  };

  const alarms = data?.response || [];

  return (
    <AlarmsContainer>
      <AlarmTable alarms={alarms} onFixClick={handleFixClick} />
      <AlarmModal
        visible={modalVisible}
        selectedAlarm={selectedAlarm}
        onHide={handleModalHide}
        onMarkAsRead={handleMarkAsRead}
        isLoading={updateNotificationRead.isPending}
      />
    </AlarmsContainer>
  );
};

export default DeviceAlarms;
